import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { hospitalsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Palette,
  Building2,
  FileText,
  Globe,
  Save,
  Loader2,
  Eye,
  Upload,
  Phone,
  Mail,
  MapPin,
} from 'lucide-react';

export const BrandingPage = () => {
  const { user, hospital: userHospital } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandingData, setBrandingData] = useState(null);
  const [hospitalInfo, setHospitalInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    phone: '',
    alternate_phone: '',
    email: '',
  });
  const [branding, setBranding] = useState({
    logo_url: '',
    tagline: '',
    gst_number: '',
    primary_color: '#0D9488',
    secondary_color: '#84CC16',
    accent_color: '#10B981',
    website: '',
    custom_domain: '',
    invoice_footer: '',
    invoice_terms: '',
  });

  const hospitalId = user?.hospital_id;
  const isSuperAdmin = user?.is_super_admin;

  useEffect(() => {
    if (hospitalId) {
      fetchBranding();
    } else {
      setLoading(false);
    }
  }, [hospitalId]);

  const fetchBranding = async () => {
    try {
      const response = await hospitalsAPI.getBranding(hospitalId);
      const data = response.data;
      
      setHospitalInfo({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
        country: data.country || 'India',
        phone: data.phone || '',
        alternate_phone: data.alternate_phone || '',
        email: data.email || '',
      });
      
      setBranding({
        logo_url: data.branding?.logo_url || '',
        tagline: data.branding?.tagline || '',
        gst_number: data.branding?.gst_number || '',
        primary_color: data.branding?.primary_color || '#0D9488',
        secondary_color: data.branding?.secondary_color || '#84CC16',
        accent_color: data.branding?.accent_color || '#10B981',
        website: data.branding?.website || '',
        custom_domain: data.branding?.custom_domain || '',
        invoice_footer: data.branding?.invoice_footer || '',
        invoice_terms: data.branding?.invoice_terms || '',
      });
      
      setBrandingData(data);
    } catch (err) {
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      await hospitalsAPI.updateInfo(hospitalId, hospitalInfo);
      toast.success('Hospital information updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update information');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await hospitalsAPI.updateBranding(hospitalId, branding);
      toast.success('Branding settings updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  if (!hospitalId && !isSuperAdmin) {
    return (
      <div className="text-center py-12" data-testid="branding-no-hospital">
        <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">No Hospital Assigned</h2>
        <p className="text-slate-500 mt-2">You need to be assigned to a hospital to configure branding.</p>
      </div>
    );
  }

  if (isSuperAdmin && !hospitalId) {
    return (
      <div className="text-center py-12" data-testid="branding-super-admin">
        <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Super Admin View</h2>
        <p className="text-slate-500 mt-2">Select a hospital from the Hospitals page to configure its branding.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="branding-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          White-Label Branding
        </h1>
        <p className="mt-1 text-slate-600">
          Customize your hospital's branding, colors, and invoice settings
        </p>
      </div>

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="info" className="data-[state=active]:bg-white">
            <Building2 className="h-4 w-4 mr-2" />
            Hospital Info
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-white">
            <Palette className="h-4 w-4 mr-2" />
            Branding & Colors
          </TabsTrigger>
          <TabsTrigger value="invoice" className="data-[state=active]:bg-white">
            <FileText className="h-4 w-4 mr-2" />
            Invoice Settings
          </TabsTrigger>
          <TabsTrigger value="preview" className="data-[state=active]:bg-white">
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </TabsTrigger>
        </TabsList>

        {/* Hospital Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Hospital Information</CardTitle>
              <CardDescription>Basic details about your hospital that appear on documents and invoices</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Hospital Name *</Label>
                  <Input
                    id="name"
                    value={hospitalInfo.name}
                    onChange={(e) => setHospitalInfo({ ...hospitalInfo, name: e.target.value })}
                    placeholder="Enter hospital name"
                    data-testid="hospital-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={hospitalInfo.email}
                      onChange={(e) => setHospitalInfo({ ...hospitalInfo, email: e.target.value })}
                      className="pl-10"
                      placeholder="hospital@example.com"
                      data-testid="hospital-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Primary Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone"
                      value={hospitalInfo.phone}
                      onChange={(e) => setHospitalInfo({ ...hospitalInfo, phone: e.target.value })}
                      className="pl-10"
                      placeholder="+91 XXXXX XXXXX"
                      data-testid="hospital-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alternate_phone">Alternate Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="alternate_phone"
                      value={hospitalInfo.alternate_phone}
                      onChange={(e) => setHospitalInfo({ ...hospitalInfo, alternate_phone: e.target.value })}
                      className="pl-10"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="address">Street Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Textarea
                    id="address"
                    value={hospitalInfo.address}
                    onChange={(e) => setHospitalInfo({ ...hospitalInfo, address: e.target.value })}
                    className="pl-10 min-h-[80px]"
                    placeholder="Enter complete street address"
                    data-testid="hospital-address-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={hospitalInfo.city}
                    onChange={(e) => setHospitalInfo({ ...hospitalInfo, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={hospitalInfo.state}
                    onChange={(e) => setHospitalInfo({ ...hospitalInfo, state: e.target.value })}
                    placeholder="State"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode">PIN Code</Label>
                  <Input
                    id="pincode"
                    value={hospitalInfo.pincode}
                    onChange={(e) => setHospitalInfo({ ...hospitalInfo, pincode: e.target.value })}
                    placeholder="XXXXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={hospitalInfo.country}
                    onChange={(e) => setHospitalInfo({ ...hospitalInfo, country: e.target.value })}
                    placeholder="India"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveInfo} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={saving}
                  data-testid="save-info-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Information
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding & Colors Tab */}
        <TabsContent value="branding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Logo & Identity</CardTitle>
                <CardDescription>Upload your logo and set your tagline</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="logo_url"
                      value={branding.logo_url}
                      onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      data-testid="logo-url-input"
                    />
                  </div>
                  {branding.logo_url && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-2">Logo Preview:</p>
                      <img 
                        src={branding.logo_url} 
                        alt="Logo preview" 
                        className="h-16 w-auto"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tagline">Tagline / Slogan</Label>
                  <Input
                    id="tagline"
                    value={branding.tagline}
                    onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
                    placeholder="Your hospital's tagline"
                    data-testid="tagline-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gst_number">GST Number</Label>
                  <Input
                    id="gst_number"
                    value={branding.gst_number}
                    onChange={(e) => setBranding({ ...branding, gst_number: e.target.value })}
                    placeholder="22AAAAA0000A1Z5"
                    data-testid="gst-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="website"
                      value={branding.website}
                      onChange={(e) => setBranding({ ...branding, website: e.target.value })}
                      className="pl-10"
                      placeholder="https://yourhospital.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Brand Colors</CardTitle>
                <CardDescription>Set your hospital's brand colors for UI customization</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary_color">Primary Color</Label>
                    <div className="flex gap-3 items-center">
                      <input
                        type="color"
                        id="primary_color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="h-10 w-16 rounded cursor-pointer"
                        data-testid="primary-color-input"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="flex-1 font-mono"
                        placeholder="#0D9488"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Used for buttons, links, and primary actions</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary_color">Secondary Color</Label>
                    <div className="flex gap-3 items-center">
                      <input
                        type="color"
                        id="secondary_color"
                        value={branding.secondary_color}
                        onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                        className="h-10 w-16 rounded cursor-pointer"
                        data-testid="secondary-color-input"
                      />
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                        className="flex-1 font-mono"
                        placeholder="#84CC16"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Used for highlights and secondary elements</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accent_color">Accent Color</Label>
                    <div className="flex gap-3 items-center">
                      <input
                        type="color"
                        id="accent_color"
                        value={branding.accent_color}
                        onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                        className="h-10 w-16 rounded cursor-pointer"
                        data-testid="accent-color-input"
                      />
                      <Input
                        value={branding.accent_color}
                        onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                        className="flex-1 font-mono"
                        placeholder="#10B981"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Used for success states and accents</p>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-3">Color Palette Preview:</p>
                  <div className="flex gap-2">
                    <div 
                      className="h-12 flex-1 rounded-lg flex items-center justify-center text-white text-xs font-medium"
                      style={{ backgroundColor: branding.primary_color }}
                    >
                      Primary
                    </div>
                    <div 
                      className="h-12 flex-1 rounded-lg flex items-center justify-center text-slate-900 text-xs font-medium"
                      style={{ backgroundColor: branding.secondary_color }}
                    >
                      Secondary
                    </div>
                    <div 
                      className="h-12 flex-1 rounded-lg flex items-center justify-center text-white text-xs font-medium"
                      style={{ backgroundColor: branding.accent_color }}
                    >
                      Accent
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={handleSaveBranding} 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={saving}
                    data-testid="save-branding-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Branding
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Invoice Settings Tab */}
        <TabsContent value="invoice">
          <Card>
            <CardHeader>
              <CardTitle>Invoice Configuration</CardTitle>
              <CardDescription>Customize how your invoices appear to patients</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="invoice_footer">Invoice Footer Text</Label>
                <Textarea
                  id="invoice_footer"
                  value={branding.invoice_footer}
                  onChange={(e) => setBranding({ ...branding, invoice_footer: e.target.value })}
                  placeholder="Thank you for choosing our hospital. Get well soon!"
                  className="min-h-[80px]"
                  data-testid="invoice-footer-input"
                />
                <p className="text-xs text-slate-500">This text will appear at the bottom of all invoices</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice_terms">Terms & Conditions</Label>
                <Textarea
                  id="invoice_terms"
                  value={branding.invoice_terms}
                  onChange={(e) => setBranding({ ...branding, invoice_terms: e.target.value })}
                  placeholder="1. Payment is due within 7 days of invoice date.&#10;2. Please bring this invoice for any follow-up visits.&#10;3. For queries, contact our billing department."
                  className="min-h-[120px]"
                  data-testid="invoice-terms-input"
                />
                <p className="text-xs text-slate-500">Terms and conditions displayed on invoices</p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="custom_domain">Custom Domain (Future Ready)</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="custom_domain"
                    value={branding.custom_domain}
                    onChange={(e) => setBranding({ ...branding, custom_domain: e.target.value })}
                    className="pl-10"
                    placeholder="hms.yourhospital.com"
                    data-testid="custom-domain-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Custom domain support will be available in a future update</p>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveBranding} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={saving}
                  data-testid="save-invoice-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Invoice Settings
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview">
          <div className="space-y-6">
            {/* Invoice Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Invoice Preview</CardTitle>
                <CardDescription>See how your invoices will look with current branding</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="border rounded-lg p-8 bg-white shadow-sm"
                  style={{ maxWidth: '800px', margin: '0 auto' }}
                >
                  {/* Invoice Header */}
                  <div className="flex justify-between items-start border-b pb-6 mb-6">
                    <div className="flex items-center gap-4">
                      {branding.logo_url ? (
                        <img src={branding.logo_url} alt="Logo" className="h-16 w-auto" />
                      ) : (
                        <div 
                          className="h-16 w-16 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                          style={{ backgroundColor: branding.primary_color }}
                        >
                          {hospitalInfo.name?.charAt(0) || 'H'}
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold" style={{ color: branding.primary_color }}>
                          {hospitalInfo.name || 'Hospital Name'}
                        </h2>
                        {branding.tagline && (
                          <p className="text-sm text-slate-500 italic">{branding.tagline}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-600">
                      <p>{hospitalInfo.address}</p>
                      <p>{hospitalInfo.city}{hospitalInfo.state ? `, ${hospitalInfo.state}` : ''} {hospitalInfo.pincode}</p>
                      <p>{hospitalInfo.phone}</p>
                      <p>{hospitalInfo.email}</p>
                      {branding.gst_number && <p className="font-medium mt-1">GSTIN: {branding.gst_number}</p>}
                    </div>
                  </div>

                  {/* Invoice Title */}
                  <div className="text-center mb-6">
                    <h1 
                      className="text-2xl font-bold"
                      style={{ color: branding.primary_color }}
                    >
                      INVOICE
                    </h1>
                    <div className="flex justify-center gap-8 mt-2 text-sm text-slate-600">
                      <span>Invoice No: <strong>INV-2024-0001</strong></span>
                      <span>Date: <strong>26 Feb 2026</strong></span>
                    </div>
                  </div>

                  {/* Patient Info */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-slate-700 mb-2">Patient Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p><span className="text-slate-500">Name:</span> John Doe</p>
                        <p><span className="text-slate-500">Patient ID:</span> PT-2024-001</p>
                      </div>
                      <div>
                        <p><span className="text-slate-500">Phone:</span> +91 98765 43210</p>
                        <p><span className="text-slate-500">Visit Date:</span> 26 Feb 2026</p>
                      </div>
                    </div>
                  </div>

                  {/* Sample Invoice Items */}
                  <table className="w-full mb-6">
                    <thead>
                      <tr style={{ backgroundColor: branding.primary_color + '15' }}>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Description</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-700">Qty</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Rate</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3 px-4">General Consultation</td>
                        <td className="py-3 px-4 text-center">1</td>
                        <td className="py-3 px-4 text-right">₹500.00</td>
                        <td className="py-3 px-4 text-right">₹500.00</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4">Blood Test - CBC</td>
                        <td className="py-3 px-4 text-center">1</td>
                        <td className="py-3 px-4 text-right">₹350.00</td>
                        <td className="py-3 px-4 text-right">₹350.00</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4">Medicines</td>
                        <td className="py-3 px-4 text-center">1</td>
                        <td className="py-3 px-4 text-right">₹250.00</td>
                        <td className="py-3 px-4 text-right">₹250.00</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-right py-3 px-4 font-semibold">Subtotal:</td>
                        <td className="py-3 px-4 text-right">₹1,100.00</td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="text-right py-3 px-4 text-slate-500">GST (18%):</td>
                        <td className="py-3 px-4 text-right text-slate-500">₹198.00</td>
                      </tr>
                      <tr style={{ backgroundColor: branding.primary_color + '15' }}>
                        <td colSpan="3" className="text-right py-3 px-4 font-bold text-lg">Total:</td>
                        <td className="py-3 px-4 text-right font-bold text-lg" style={{ color: branding.primary_color }}>
                          ₹1,298.00
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Terms */}
                  {branding.invoice_terms && (
                    <div className="bg-slate-50 rounded-lg p-4 mb-4 text-sm text-slate-600">
                      <h4 className="font-semibold text-slate-700 mb-2">Terms & Conditions:</h4>
                      <p className="whitespace-pre-line">{branding.invoice_terms}</p>
                    </div>
                  )}

                  {/* Footer */}
                  {branding.invoice_footer && (
                    <div 
                      className="text-center pt-4 border-t text-sm"
                      style={{ color: branding.primary_color }}
                    >
                      {branding.invoice_footer}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrandingPage;
